/**
 * Maps backend theme configuration keys (suffix after light_/dark_ prefix)
 * to CSS variable names and optional setter behaviour.
 * Used when applying configuration.light / configuration.dark from the theme API.
 */
export const CONFIGURATION_COLOUR_SUFFIXES = [
  'primary_colour',
  'secondary_colour',
  'background_colour',
  'text_colour',
  'link_colour',
  'button_background_colour',
  'button_hover_colour',
  'button_active_colour',
  'button_text_colour',
  'input_background_colour',
];

const PREFIX_LIGHT = 'light_';
const PREFIX_DARK = 'dark_';

/**
 * Mapping from backend key suffix to CSS variable(s) to set.
 * Value is either a string (single var) or an array of var names.
 * Special case: background_colour is handled in applyConfigurationColours with setBackground.
 */
export const SUFFIX_TO_CSS_VARS = {
  primary_colour: ['--primary'],
  secondary_colour: ['--secondary'],
  background_colour: ['--background', '--settings-overlay-background', '--dropdown-background'],
  text_colour: ['--text-color', '--footer-text-color', '--input-place-holder-text-color', '--fills-primary-foreground'],
  link_colour: ['--a-text-color'],
  button_background_colour: ['--button-color'],
  button_hover_colour: ['--button-color-hover'],
  button_active_colour: ['--button-color-active'],
  button_text_colour: ['--button-text-color'],
  input_background_colour: ['--input-background'],
};

/**
 * Normalises a configuration object (light_* or dark_* keys) to a map of suffix -> value.
 * @param {Record<string, string>} config - configuration.light or configuration.dark
 * @param {string} prefix - 'light_' or 'dark_'
 * @returns {Record<string, string>} suffix -> colour value
 */
export const normaliseConfigurationKeys = (config, prefix) => {
  if (!config || typeof config !== 'object') {
    return {};
  }
  const result = {};
  Object.entries(config).forEach(([key, value]) => {
    if (key !== 'colours'
        && typeof key === 'string'
        && key.startsWith(prefix)
        && value != null
        && value !== '') {
      const suffix = key.slice(prefix.length);
      if (CONFIGURATION_COLOUR_SUFFIXES.includes(suffix)) {
        result[suffix] = value;
      }
    }
  });
  return result;
};

/**
 * Maps API nested `colours` object to internal suffix -> value map
 * (same keys as legacy flat format). Values passed through unchanged (no hex repair).
 * @param {Record<string, unknown>} colours - configuration.light|dark.colours
 * @returns {Record<string, string>}
 */
export const coloursObjectToSuffixMap = (colours) => {
  if (!colours || typeof colours !== 'object') {
    return {};
  }
  const result = {};
  const put = (suffix, value) => {
    if (value != null && value !== '' && CONFIGURATION_COLOUR_SUFFIXES.includes(suffix)) {
      result[suffix] = value;
    }
  };

  put('primary_colour', colours.primary);
  put('secondary_colour', colours.secondary);
  put('background_colour', colours.background);
  put('text_colour', colours.text);
  put('link_colour', colours.link);
  put('input_background_colour', colours.input_background);

  const { button } = colours;
  if (button && typeof button === 'object') {
    put('button_background_colour', button.background);
    put('button_hover_colour', button.hover);
    put('button_active_colour', button.active);
    put('button_text_colour', button.text);
  }

  return result;
};

/**
 * Root-level theme fields (API) used when per-mode nested `colours` omits or nulls a value.
 * Button/input slots have no documented root equivalents — omitted from this map.
 * @param {string} suffix - CONFIGURATION_COLOUR_SUFFIXES entry
 * @param {object} [theme] - merchant theme root
 * @returns {string|undefined}
 */
const getRootColourFallbackForSuffix = (suffix, theme) => {
  if (!theme || typeof theme !== 'object') {
    return undefined;
  }
  switch (suffix) {
    case 'primary_colour':
      return theme.primary;
    case 'secondary_colour':
      return theme.secondary;
    case 'background_colour':
      return theme.background;
    case 'text_colour':
      return theme.textColor ?? theme.text_colour;
    case 'link_colour':
      return theme.linkColor ?? theme.link_colour;
    default:
      return undefined;
  }
};

/**
 * Fills missing or empty suffix keys from theme root so CSS application matches
 * getPaletteTripleFromModeBlock / API semantics when nested colours are null.
 * @param {Record<string, string>} merged - legacy + nested suffix map for one mode
 * @param {object} [theme] - full merchant theme (root palette)
 * @returns {Record<string, string>}
 */
export const fillMissingColourSuffixesFromThemeRoot = (merged, theme) => {
  if (!merged || typeof merged !== 'object') {
    return merged;
  }
  const out = { ...merged };
  const needsFill = (v) => v == null || v === '';
  CONFIGURATION_COLOUR_SUFFIXES.forEach((suffix) => {
    if (needsFill(out[suffix])) {
      const fallback = getRootColourFallbackForSuffix(suffix, theme);
      if (fallback != null && fallback !== '') {
        out[suffix] = fallback;
      }
    }
  });
  return out;
};

export const getConfigurationWithPrefix = (theme, isLightMode) => {
  const configuration = theme?.configuration;
  if (!configuration) {
    return null;
  }
  const config = isLightMode ? configuration.light : configuration.dark;
  const prefix = isLightMode ? PREFIX_LIGHT : PREFIX_DARK;
  if (!config) {
    return null;
  }
  const legacyMap = normaliseConfigurationKeys(config, prefix);
  const nestedMap = config.colours && typeof config.colours === 'object'
    ? coloursObjectToSuffixMap(config.colours)
    : {};
  const merged = { ...legacyMap, ...nestedMap };
  return fillMissingColourSuffixesFromThemeRoot(merged, theme);
};

/**
 * True if either light or dark configuration defines a non-empty background colour
 * (nested `colours.background` or legacy flat keys). Used to disable background gradients.
 * @param {object} [theme] - merchant theme object
 */
export const configurationHasBackgroundInEitherMode = (theme) => {
  const configuration = theme?.configuration;
  if (!configuration?.light || !configuration?.dark) {
    return false;
  }
  const lightMap = getConfigurationWithPrefix(theme, true);
  const darkMap = getConfigurationWithPrefix(theme, false);
  const hasBg = (map) => map != null
    && map.background_colour != null
    && map.background_colour !== '';
  return hasBg(lightMap) || hasBg(darkMap);
};

/**
 * Resolves primary, secondary, background for the active mode block
 * (nested colours or legacy flat keys).
 * @param {object} [block] - configuration.light or configuration.dark
 * @param {string} prefix - 'light_' or 'dark_'
 * @param {object} [themeRoot] - merchant theme root (flat palette)
 * @param {string} defaultBg
 */
export const getPaletteTripleFromModeBlock = (block, prefix, themeRoot, defaultBg) => {
  const colours = block?.colours;
  if (colours && typeof colours === 'object') {
    return {
      primary: colours.primary ?? themeRoot?.primary ?? '#888888',
      secondary: colours.secondary ?? themeRoot?.secondary ?? '#888888',
      background: colours.background ?? themeRoot?.background ?? defaultBg,
    };
  }
  return {
    primary: block?.[`${prefix}primary_colour`] ?? themeRoot?.primary ?? '#888888',
    secondary: block?.[`${prefix}secondary_colour`] ?? themeRoot?.secondary ?? '#888888',
    background: block?.[`${prefix}background_colour`] ?? themeRoot?.background ?? defaultBg,
  };
};
